import React, { useState, useRef } from 'react';
import { makeStyles, Switch } from '@fluentui/react-components';
import {
  ImageEdit20Regular,
  Copy20Regular,
  Delete20Regular,
  ArrowUp20Regular,
  ArrowDown20Regular,
  MoreHorizontal20Regular,
} from '@fluentui/react-icons';
import { NPSQuestion as NPSQuestionType } from '../../../../types/forms';

const useStyles = makeStyles({
  questionCard: {
    display: 'flex',
    flexDirection: 'column',
    backgroundColor: '#f5f5f5',
    borderRadius: '4px',
    width: '100%',
    position: 'relative',
  },
  topBar: {
    height: '5px',
    backgroundColor: '#03787c',
    borderTopLeftRadius: '4px',
    borderTopRightRadius: '4px',
  },
  contentWrapper: {
    display: 'flex',
    flexDirection: 'column',
    gap: '10px',
    paddingBottom: '10px',
  },
  canvas: {
    display: 'flex',
    flexDirection: 'column',
    gap: '28px',
    padding: '0 20px',
  },
  actionButtons: {
    display: 'flex',
    gap: '28px',
    alignItems: 'center',
    justifyContent: 'flex-end',
    cursor: 'pointer',
    paddingTop: '16px',
  },
  actionIcon: {
    fontSize: '20px',
    color: '#616161',
    cursor: 'pointer',
    '&:hover': {
      color: '#03787c',
    },
  },
  questionSection: {
    display: 'flex',
    flexDirection: 'column',
    gap: '20px',
    width: '100%',
  },
  questionHeader: {
    display: 'flex',
    flexDirection: 'column',
    gap: '16px',
    width: '100%',
  },
  questionTitleRow: {
    display: 'flex',
    gap: '8px',
    alignItems: 'center',
    width: '100%',
  },
  questionNumber: {
    width: '20px',
    height: '20px',
    fontFamily: '"Segoe UI", sans-serif',
    fontSize: '14px',
    fontWeight: 600,
    lineHeight: '20px',
    color: '#292929',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    flexShrink: 0,
  },
  questionInput: {
    flex: 1,
    height: '32px',
    backgroundColor: 'white',
    borderRadius: '4px',
    position: 'relative',
    '& input': {
      border: 'none',
      outline: 'none',
      width: '100%',
      height: '100%',
      padding: '6px 40px 6px 10px',
      fontFamily: '"Segoe UI", sans-serif',
      fontSize: '14px',
      color: '#242424',
      backgroundColor: 'transparent',
    },
  },
  inputFocusBorder: {
    position: 'absolute',
    bottom: 0,
    left: 0,
    right: 0,
    height: '2px',
    backgroundColor: '#02787c',
    borderBottomLeftRadius: '4px',
    borderBottomRightRadius: '4px',
  },
  imageEditIcon: {
    position: 'absolute',
    right: '10px',
    top: '50%',
    transform: 'translateY(-50%)',
    cursor: 'pointer',
    color: '#616161',
    '&:hover': {
      color: '#03787c',
    },
  },
  npsScale: {
    paddingLeft: '20px',
    paddingRight: '10px',
    width: 'calc(100% - 30px)',
    display: 'flex',
    flexDirection: 'column',
    gap: '12px',
  },
  scaleRow: {
    display: 'flex',
    gap: '0',
    alignItems: 'stretch',
    width: '100%',
  },
  scaleNumber: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    gap: '4px',
    flex: 1,
    height: '40px',
    backgroundColor: 'white',
    border: '1px solid #d1d1d1',
    borderRight: 'none',
    '&:first-child': {
      borderTopLeftRadius: '4px',
      borderBottomLeftRadius: '4px',
    },
    '&:last-child': {
      borderRight: '1px solid #d1d1d1',
      borderTopRightRadius: '4px',
      borderBottomRightRadius: '4px',
    },
  },
  numberLabel: {
    fontFamily: '"Segoe UI", sans-serif',
    fontSize: '12px',
    fontWeight: 600,
    color: '#616161',
  },
  labelsRow: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
  },
  label: {
    fontFamily: '"Segoe UI", sans-serif',
    fontSize: '12px',
    color: '#616161',
    backgroundColor: 'white',
    borderRadius: '4px',
    position: 'relative',
    height: '32px',
    display: 'flex',
    alignItems: 'center',
    '& input': {
      border: 'none',
      outline: 'none',
      width: '100%',
      height: '100%',
      padding: '0 10px',
      fontFamily: '"Segoe UI", sans-serif',
      fontSize: '12px',
      color: '#616161',
      backgroundColor: 'transparent',
    },
  },
  labelFocusBorder: {
    position: 'absolute',
    bottom: 0,
    left: 0,
    right: 0,
    height: '2px',
    backgroundColor: '#02787c',
    borderBottomLeftRadius: '4px',
    borderBottomRightRadius: '4px',
  },
  divider: {
    width: '100%',
    height: '1px',
    backgroundColor: '#e1e1e1',
  },
  settingsBar: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    padding: '0 24px 0 16px',
    height: '44px',
  },
  settingsGroup: {
    display: 'flex',
    gap: '8px',
    alignItems: 'center',
  },
  toggleGroup: {
    display: 'flex',
    gap: '12px',
    alignItems: 'center',
    padding: '8px',
  },
  toggleLabel: {
    fontFamily: '"Segoe UI", sans-serif',
    fontSize: '14px',
    fontWeight: 400,
    lineHeight: '20px',
    color: '#242424',
  },
  moreIcon: {
    cursor: 'pointer',
    color: '#616161',
    '&:hover': {
      color: '#03787c',
    },
  },
});

// Copilot icon component
const CopilotIcon: React.FC<{ className?: string }> = ({ className }) => (
  <img 
    src="/src/assets/office/ribbon/Copilot.svg" 
    alt="Copilot" 
    className={className}
    style={{ width: '20px', height: '20px' }}
  />
);

interface NPSQuestionProps {
  question: NPSQuestionType;
  questionNumber: number;
  onChange: (question: NPSQuestionType) => void;
  onDelete: () => void;
  onDuplicate: () => void;
  onMoveUp: () => void;
  onMoveDown: () => void;
}

export const NPSQuestion: React.FC<NPSQuestionProps> = ({
  question,
  questionNumber,
  onChange,
  onDelete,
  onDuplicate,
  onMoveUp,
  onMoveDown,
}) => {
  const styles = useStyles();
  const [isTitleFocused, setIsTitleFocused] = useState(false);
  const [isLowLabelFocused, setIsLowLabelFocused] = useState(false);
  const [isHighLabelFocused, setIsHighLabelFocused] = useState(false);
  const titleInputRef = useRef<HTMLInputElement>(null);

  const handleTitleChange = (value: string) => {
    onChange({ ...question, title: value });
  };

  const handleLowLabelChange = (value: string) => {
    onChange({ ...question, lowLabel: value });
  };

  const handleHighLabelChange = (value: string) => {
    onChange({ ...question, highLabel: value });
  };

  const handleToggleRequired = () => {
    onChange({ ...question, required: !question.required });
  };

  return (
    <div className={styles.questionCard}>
      <div className={styles.topBar} />
      
      <div className={styles.contentWrapper}>
        <div className={styles.canvas}>
          {/* Action Buttons */}
          <div className={styles.actionButtons}>
            <CopilotIcon className={styles.actionIcon} />
            <Copy20Regular className={styles.actionIcon} onClick={onDuplicate} />
            <Delete20Regular className={styles.actionIcon} onClick={onDelete} />
            <ArrowUp20Regular className={styles.actionIcon} onClick={onMoveUp} />
            <ArrowDown20Regular className={styles.actionIcon} onClick={onMoveDown} />
          </div>

          <div className={styles.questionSection}>
            <div className={styles.questionHeader}>
              {/* Question Title */}
              <div className={styles.questionTitleRow}>
                <div className={styles.questionNumber}>{questionNumber}.</div>
                <div className={styles.questionInput}>
                  <input
                    ref={titleInputRef}
                    type="text"
                    value={question.title}
                    onChange={(e) => handleTitleChange(e.target.value)}
                    onFocus={() => setIsTitleFocused(true)}
                    onBlur={() => setIsTitleFocused(false)}
                    placeholder="Question"
                  />
                  {isTitleFocused && <div className={styles.inputFocusBorder} />}
                  <ImageEdit20Regular className={styles.imageEditIcon} />
                </div>
              </div>
            </div>

            {/* NPS Scale */}
            <div className={styles.npsScale}>
              <div className={styles.scaleRow}>
                {[0, 1, 2, 3, 4, 5, 6, 7, 8, 9, 10].map((num) => (
                  <div key={num} className={styles.scaleNumber}>
                    <span className={styles.numberLabel}>{num}</span>
                  </div>
                ))}
              </div>
              
              <div className={styles.labelsRow}>
                <div className={styles.label}>
                  <input
                    type="text"
                    value={question.lowLabel || ''}
                    onChange={(e) => handleLowLabelChange(e.target.value)}
                    onFocus={() => setIsLowLabelFocused(true)}
                    onBlur={() => setIsLowLabelFocused(false)}
                    placeholder="Not at all likely"
                  />
                  {isLowLabelFocused && <div className={styles.labelFocusBorder} />}
                </div>
                <div className={styles.label}>
                  <input
                    type="text"
                    value={question.highLabel || ''}
                    onChange={(e) => handleHighLabelChange(e.target.value)}
                    onFocus={() => setIsHighLabelFocused(true)}
                    onBlur={() => setIsHighLabelFocused(false)}
                    placeholder="Extremely likely"
                  />
                  {isHighLabelFocused && <div className={styles.labelFocusBorder} />}
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Divider */}
        <div className={styles.divider} />

        {/* Settings Bar */}
        <div className={styles.settingsBar}>
          <div />
          <div className={styles.settingsGroup}>
            <div className={styles.toggleGroup}>
              <Switch
                checked={question.required}
                onChange={handleToggleRequired}
              />
              <span className={styles.toggleLabel}>Required</span>
            </div>
            <MoreHorizontal20Regular className={styles.moreIcon} />
          </div>
        </div>
      </div>
    </div>
  );
};
